import { useCallback } from 'react'

import {
  Card,
  Heading,
  SkeletonBox,
  TextLink,
  DescriptionList,
} from '@circlefin/components'
import { useDate } from '@circlefin/hooks'
import { useModal } from '@circlefin/modal-router'
import { usePermission } from '@circlefin/permissions/react'
import { CHANGE_PASSWORD } from '@services/permissions'
import { changePassword } from '@services/sections/lib/settings'
import { mfa } from '@services/sections/modal/mfa/mfa'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  FactorStatus,
  FactorType,
  useFactorQuery,
  useUserQuery,
} from '@shared/graphql'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { ActiveFactor } from '../ActiveFactor/ActiveFactor'

export const LoginAndSecurityList: React.FC = () => {
  const { t } = useTranslation('settings')
  const { date } = useDate()
  const router = useRouter()
  const modal = useModal()

  const [, { isAuthorized }] = usePermission(CHANGE_PASSWORD)

  const { data, loading, error, refetch } = useUserQuery({
    notifyOnNetworkStatusChange: true,
  })

  const { data: pinCode } = useFactorQuery({
    variables: {
      factorType: FactorType.PIN_CODE,
    },
  })

  const handleChangePassword = useCallback(() => {
    void router.push(changePassword.route)
  }, [router])

  const handleUpdatePinCode = useCallback(() => {
    modal.router.push(mfa.updatePinCode)
  }, [modal.router])

  return (
    <Card>
      <Card.Content className="pb-8 pl-8 pt-5">
        <Heading variant="title/sm">
          <Heading.Header as="h3">
            {t('security.loginAndSecurity.login')}
          </Heading.Header>
        </Heading>

        <DescriptionList density="loose">
          <DescriptionList.Label
            className="w-40"
            data-testid="login-security-info-password"
          >
            {t('security.loginAndSecurity.password.label')}
          </DescriptionList.Label>
          <DescriptionList.Description className="flex flex-row justify-between">
            <GraphQLErrorBoundary
              error={error}
              retry={refetch}
              variant="component"
            >
              <div className="flex flex-col">
                <SkeletonBox className="block h-5 w-36" loading={loading}>
                  {t('security.loginAndSecurity.password.description')}
                </SkeletonBox>
                <SkeletonBox className="mt-2 block h-3 w-40" loading={loading}>
                  {data?.user.passwordChanged && (
                    <p className="text-neutral-subtle">
                      {t('security.loginAndSecurity.password.lastUpdated', {
                        date: date(data?.user.passwordChanged),
                      })}
                    </p>
                  )}
                </SkeletonBox>
              </div>

              {!loading && isAuthorized && (
                <TextLink onClick={handleChangePassword} variant="primary">
                  {t('security.loginAndSecurity.password.update')}
                </TextLink>
              )}
            </GraphQLErrorBoundary>
          </DescriptionList.Description>

          <ActiveFactor factorType={FactorType.TOKEN_SOFTWARE_TOTP} />

          <ActiveFactor factorType={FactorType.PUSH} />
        </DescriptionList>

        {pinCode?.factor?.status === FactorStatus.ACTIVE && (
          <>
            <Heading className="mt-8" variant="title/sm">
              <Heading.Header as="h3">
                {t('security.loginAndSecurity.sensitiveActions')}
              </Heading.Header>
            </Heading>

            <DescriptionList density="loose">
              <ActiveFactor factorType={FactorType.PIN_CODE}>
                <TextLink onClick={handleUpdatePinCode} variant="primary">
                  {t('security.loginAndSecurity.pinCode.reset')}
                </TextLink>
              </ActiveFactor>
            </DescriptionList>
          </>
        )}
      </Card.Content>
    </Card>
  )
}
